// features/email-tracking/email-tracking.service.ts
//
// Data layer for the email-status timeline (Sent / Delivered / Opened) of an
// invoice, customer offer or customer contract.
//
// Backend (auth: Bearer JWT, role Employee — same as the lists themselves):
//   GET /SendGridWebhook/invoice/{id}
//   GET /SendGridWebhook/customer-offer/{id}
//   GET /SendGridWebhook/customer-contract/{id}
// `id` is always the internal numeric id (InvoiceItem.id / offer id / contract
// id), NOT the user-facing number. All three return an EmailTrackingStatusVM
// (raw SendGrid events, newest-first).
//
// This service adapts that raw response into the EmailTrackingDetails shape the
// modal consumes (fixed Sent -> Delivered -> Opened steps + a furthest status).
// The trailing timeline nodes are NOT part of this call — the modal derives them
// from the row's own status flags (Paid / Salary Created / Salary Paid for an
// invoice, Signed for an offer or contract).
//
// The v2 apiClient is used (has 401 handling + bearer-token injection).

import { get } from '../../api/api.service';
import {
    EmailStatus,
    EmailTrackingDetails,
    EmailTrackingEvent,
    EmailTrackingKind,
    EmailTrackingStatusVM,
    RawEmailTrackingEvent,
} from './email-tracking.types';

// ---------------------------------------------------------------------------
// Adapter: raw SendGrid response -> EmailTrackingDetails (email steps only)
// ---------------------------------------------------------------------------

/** The three positional email steps, always in Sent -> Delivered -> Opened order. */
const STEP_TYPES: EmailTrackingEvent['type'][] = ['sent', 'delivered', 'opened'];

const norm = (event: string): string => (event ?? '').trim().toLowerCase();

/** First event (any position) whose raw name matches one of `names`, or undefined. */
const findEvent = (
    events: RawEmailTrackingEvent[],
    names: string[]
): RawEmailTrackingEvent | undefined => events.find((e) => names.includes(norm(e.event)));

const hasEvent = (events: RawEmailTrackingEvent[], names: string[]): boolean =>
    events.some((e) => names.includes(norm(e.event)));

/**
 * Furthest email stage reached, derived by precedence over the WHOLE event list
 * (not just the newest event). Caps at `opened`; bounce/dropped/spamreport map
 * to the failure statuses; opt-out events imply the mail was delivered.
 */
const deriveStatus = (events: RawEmailTrackingEvent[]): EmailStatus => {
    if (events.length === 0) return 'not_sent';
    if (hasEvent(events, ['open', 'click'])) return 'opened';
    if (hasEvent(events, ['delivered'])) return 'delivered';
    if (hasEvent(events, ['bounce'])) return 'bounced';
    if (hasEvent(events, ['dropped', 'spamreport'])) return 'failed';
    if (hasEvent(events, ['processed', 'deferred'])) return 'sent';
    if (hasEvent(events, ['unsubscribe', 'group_unsubscribe', 'group_resubscribe'])) return 'delivered';
    return 'sent';
};

/**
 * Map the backend EmailTrackingStatusVM into the EmailTrackingDetails shape the
 * modal already consumes. Fills the three positional email steps with the
 * timestamp of the matching raw event (or null when that step has not occurred).
 */
export const mapEmailTrackingResponse = (vm: EmailTrackingStatusVM): EmailTrackingDetails => {
    const events = Array.isArray(vm?.events) ? vm.events : [];
    const status = deriveStatus(events);

    const processedAt = findEvent(events, ['processed'])?.createdDate ?? vm?.createdDate ?? null;
    const deliveredAt = findEvent(events, ['delivered'])?.createdDate ?? null;
    const openedAt = findEvent(events, ['open', 'click'])?.createdDate ?? null;
    const stepTimestamp: Record<EmailTrackingEvent['type'], string | null> = {
        sent: processedAt,
        delivered: deliveredAt,
        opened: openedAt,
    };

    const timelineEvents: EmailTrackingEvent[] = STEP_TYPES.map((type) => ({
        type,
        timestamp: stepTimestamp[type],
    }));

    return {
        invoiceNumber: vm?.invoiceNumber != null ? String(vm.invoiceNumber) : '',
        status,
        sentTo: vm?.trackedEmailAddress ?? '',
        sentOn: processedAt,
        events: timelineEvents,
    };
};

/** The details object for a document that has never had a tracked email. */
const notSentDetails = (): EmailTrackingDetails => ({
    invoiceNumber: '',
    status: 'not_sent',
    sentTo: '',
    sentOn: null,
    events: STEP_TYPES.map((type) => ({ type, timestamp: null })),
});

// ---------------------------------------------------------------------------
// Service API
// ---------------------------------------------------------------------------

/**
 * Fetch the email-status timeline for one invoice, customer offer or customer
 * contract by its internal numeric id. `kind` is the URL segment, so the three
 * backend routes are reached through this one function.
 *
 * A 404 means no tracked email exists for that document — never sent, or (for
 * offers/contracts) not owned by the caller. Both are treated as a valid "not
 * sent yet" state rather than an error, so the modal renders an empty timeline
 * instead of the error panel.
 */
export const getEmailTrackingApi = async (
    id: number,
    kind: EmailTrackingKind = 'invoice'
): Promise<EmailTrackingDetails> => {
    try {
        const raw = await get(`/SendGridWebhook/${kind}/${id}`);
        // Handle both a bare EmailTrackingStatusVM and the shared
        // { statusCode, message, response } envelope other endpoints use.
        const body = raw?.response ?? raw;
        const vm = (Array.isArray(body) ? body[0] : body) as EmailTrackingStatusVM | undefined;

        if (!vm) {
            return notSentDetails();
        }

        return mapEmailTrackingResponse(vm);
    } catch (err: any) {
        if (err?.response?.status === 404) {
            return notSentDetails();
        }
        throw err;
    }
};
